import Link from 'next/link';
import React from 'react';

import { Anchor, Card, Grid, List, ListItem, Stack, Text } from '@mantine/core';
import { z } from 'zod';

import { UserHelpers } from 'helpers/UserHelpers';

import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { TextField } from 'components/forms/fields/TextField';
import { DEPHorizontal } from 'components/logo/DEPHorizontal';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { AuthenticationService } from 'services/AuthenticationService';
import { LocalStorageService } from 'services/LocalStorageService';

import cx from './HeroCard.module.scss';

export const HeroCard = ({ loginForm = <HeroLoginForm /> }: { loginForm?: React.ReactNode }) => (
    <Grid columns={3} gap={0}>
        <Grid.Col span={{ base: 3, md: 2 }} order={{ md: 2 }}>
            <h1 className={cx.title}>
                <span>Powerful Software for Power Systems & Microgrids</span>
                <small className={`${cx.tagline} gradient-cyan-green`}>Software Platform for Professionals</small>
            </h1>
        </Grid.Col>
        <Grid.Col span={{ base: 3, md: 1 }} order={{ md: 1 }}>
            <Card p="xl">{loginForm}</Card>
        </Grid.Col>
    </Grid>
);

const HeroLoginForm = () => {
    const { logoUrl, name: workspaceName } = useWorkspaceConfig();

    return (
        <Stack ta="left">
            {logoUrl ? (
                <img src={logoUrl} alt={workspaceName || 'Logo'} className={cx.workspaceLogo} />
            ) : (
                <DEPHorizontal className={cx.logo} />
            )}
            <Text mt="sm">
                When it comes to power system design, DEP helps you <strong>decide</strong>, how much of what you need
                and who can get it done!
            </Text>
            <List>
                <ListItem>
                    Create professional, content-rich <strong>single-line diagrams</strong>
                </ListItem>
                <ListItem>
                    Find <strong>professionals and products</strong> to work with
                </ListItem>
                <ListItem>
                    <strong>Control</strong> multi-vendor power systems
                </ListItem>
            </List>
            <Text>
                Everything is <strong>DC-First</strong>. We live in a DC world with a legacy AC infrastructure. From
                solar and batteries to EVs and data centers, we prioritize native Direct Current design.
            </Text>
            <Form
                defaultValues={{
                    email: '',
                }}
                zodSchema={z.object({
                    email: z.string().toLowerCase().email('Please enter a valid email'),
                })}
                onSubmit={(values) => {
                    LocalStorageService.store(UserHelpers.localStorageKey.email, values.email);
                    AuthenticationService.navigate.login();
                }}
            >
                <TextField name="email" type="email" size="md" placeholder="Enter your email" />
                <FormSubmit w="100%" mt="xs" size="md">
                    Sign Up
                </FormSubmit>
            </Form>
            <Text ta="center">
                Already have an account?{' '}
                <Anchor component={Link} href="/login" fw={600}>
                    Log In
                </Anchor>
            </Text>
        </Stack>
    );
};
